<template>
  <nav class="bui-tbl-pagination" :aria-label="ariaLabel" data-testid="list-pagination">
    <button
      v-if="showPrev"
      type="button"
      class="pill prev"
      :disabled="prevDisabled"
      data-testid="list-pagination-prev"
      @click="goTo(currentPage - 1)"
    >
      <PhCaretLeft :size="16" weight="bold" />
      <span>Prev</span>
    </button>

    <ol class="numbers">
      <li
        v-for="entry in renderedPages"
        :key="entry.kind === 'page' ? `p-${entry.value}` : `gap-${entry.id}`"
        class="num-cell"
        :class="{ active: entry.kind === 'page' && entry.value === currentPage }"
      >
        <button
          v-if="entry.kind === 'page'"
          type="button"
          class="num"
          :aria-current="entry.value === currentPage ? 'page' : undefined"
          :data-testid="`list-pagination-page-${entry.value}`"
          @click="goTo(entry.value)"
        >
          {{ entry.value }}
        </button>
        <span v-else class="ellipsis" aria-hidden="true">…</span>
      </li>
    </ol>

    <button
      type="button"
      class="pill next"
      :disabled="nextDisabled"
      data-testid="list-pagination-next"
      @click="goTo(currentPage + 1)"
    >
      <span>Next</span>
      <PhCaretRight :size="16" weight="bold" />
    </button>
  </nav>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { PhCaretLeft, PhCaretRight } from '@phosphor-icons/vue';

const props = withDefaults(
  defineProps<{
    currentPage: number;
    totalPages: number;
    ariaLabel?: string;
    /** Hide the Prev button on page 1, matching Figma `state=page1`. */
    autoHidePrev?: boolean;
    /** Max number of page chips to render around the current page. */
    visibleWindow?: number;
  }>(),
  {
    ariaLabel: 'Pagination',
    autoHidePrev: true,
    visibleWindow: 4,
  },
);

const emit = defineEmits<{ change: [page: number] }>();

const showPrev = computed(() => !(props.autoHidePrev && props.currentPage <= 1));
const prevDisabled = computed(() => props.currentPage <= 1);
const nextDisabled = computed(() => props.currentPage >= props.totalPages);

type Entry = { kind: 'page'; value: number } | { kind: 'gap'; id: string };

const renderedPages = computed<Entry[]>(() => {
  const total = Math.max(1, props.totalPages);
  const cur = Math.min(Math.max(1, props.currentPage), total);
  const win = Math.max(2, props.visibleWindow);

  if (total <= win + 2) {
    return Array.from({ length: total }, (_, i) => ({ kind: 'page' as const, value: i + 1 }));
  }

  const set = new Set<number>([1, total, cur, cur - 1, cur + 1]);
  // Pad up to the visible-window count by expanding around current.
  for (let off = 2; off <= win; off++) {
    if (set.size >= win + 2) break;
    set.add(Math.max(2, cur - off));
    if (set.size >= win + 2) break;
    set.add(Math.min(total - 1, cur + off));
  }
  const sorted = [...set].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out: Entry[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      out.push({ kind: 'gap', id: `${sorted[i - 1]}-${sorted[i]}` });
    }
    out.push({ kind: 'page', value: sorted[i] });
  }
  return out;
});

function goTo(page: number) {
  const clamped = Math.min(Math.max(1, page), props.totalPages);
  if (clamped === props.currentPage) return;
  emit('change', clamped);
}
</script>

<style lang="scss" scoped>
.bui-tbl-pagination {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

@media (max-width: 768px) {
  .bui-tbl-pagination {
    flex-wrap: wrap;
    justify-content: center;
    max-width: 100%;
    gap: 6px;
  }

  .pill {
    height: 32px;
    padding: 0 10px;
    font-size: 12px;
    line-height: 15px;
  }

  .numbers {
    flex-wrap: wrap;
    justify-content: center;
    row-gap: 6px;
    gap: 6px;
  }

  .num {
    font-size: 12px;
    line-height: 15px;
    padding: 2px 4px;
  }

  .ellipsis {
    font-size: 12px;
  }
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 14px;
  background: var(--bui-page-pill-fill, rgba(255, 252, 253, 0.32));
  color: var(--bui-page-pill-text, #fffcfd);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  line-height: 17px;

  :deep(svg) {
    width: 16px;
    height: 16px;
  }

  &:hover:not(:disabled) {
    background: var(--bui-ter-fill-hover, rgba(255, 252, 253, 0.4));
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
}

.numbers {
  display: inline-flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 12px;
}

.num-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
}

.num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  color: var(--bui-page-num-inactive, rgba(255, 252, 253, 0.6));
  font-family: inherit;
  font-size: 14px;
  line-height: 17px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;

  &:hover {
    color: var(--bui-page-num-active, #fffcfd);
  }
}

.num-cell.active .num {
  color: var(--bui-page-num-active, #fffcfd);
  font-weight: 700;
}

.ellipsis {
  color: var(--bui-page-num-inactive, rgba(255, 252, 253, 0.6));
  font-size: 14px;
}
</style>
